import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function getToken() { return localStorage.getItem('admin_token'); }

async function request(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...options.headers
    }
  });
  return res.json();
}

const SETTINGS_CONFIG = [
  { key:'min_deposit_cents',          label:'Minimum Deposit',              type:'cents',   description:'Minimum amount users can deposit' },
  { key:'min_cashout_cents',          label:'Minimum Cashout',              type:'cents',   description:'Minimum amount users can cash out' },
  { key:'min_send_cents',             label:'Minimum Send',                 type:'cents',   description:'Minimum amount users can send' },
  { key:'send_weekly_limit_cents',    label:'P2P Send — Weekly Limit',      type:'cents',   description:'Max person-to-person send per user per week' },
  { key:'cashout_single_limit_cents', label:'Cashout — Single Transaction Limit', type:'cents', description:'Max single bank cashout per transaction' },
  { key:'cashout_weekly_limit_cents', label:'Cashout — Weekly Limit',       type:'cents',   description:'Max bank cashout per user per week' },
  { key:'guest_daily_limit_cents',    label:'Guest Pay — Daily Limit',      type:'cents',   description:'Max guest payment per IP address per day' },
  { key:'guest_weekly_limit_cents',   label:'Guest Pay — Weekly Limit',     type:'cents',   description:'Max guest payment per IP address per week' },
  { key:'maintenance_mode',           label:'Maintenance Mode',             type:'boolean', description:'Disable all transactions platform-wide' },
];

export default function SettingsPage() {
  const [settings, setSettings]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({});
  const [msgs, setMsgs]           = useState({});
  const [feePct, setFeePct]       = useState(0.50);
  const [boostPct, setBoostPct]   = useState(20.00);
  const [boostSaving, setBoostSaving] = useState(false);
  const [boostMsg, setBoostMsg]   = useState('');
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeMsg, setFeeMsg]       = useState('');

  useEffect(() => {
    request('/admin/settings')
      .then(data => {
        setSettings(data);
        const rawFee = data.fee_bps !== undefined && data.fee_bps !== null ? parseFloat(data.fee_bps) : 50;
        setFeePct(rawFee / 100);
        setBoostPct((data.boost_bps !== undefined && data.boost_bps !== null ? parseFloat(data.boost_bps) : 0) / 100);
        setLoading(false);
      })
      .catch(console.error);
  }, []);

  async function handleSave(key, value) {
    setSaving(prev => ({...prev, [key]: true}));
    try {
      await request('/admin/settings', { method: 'PUT', body: JSON.stringify({ key, value }) });
      setMsgs(prev => ({...prev, [key]: 'Saved ✓'}));
      setTimeout(() => setMsgs(prev => ({...prev, [key]: ''})), 2000);
    } catch {
      setMsgs(prev => ({...prev, [key]: 'Failed'}));
    } finally {
      setSaving(prev => ({...prev, [key]: false}));
    }
  }

  async function handleFeeSave() {
    setFeeSaving(true); setFeeMsg('');
    try {
      const feeBps = Math.round(feePct * 100);
      await request('/admin/settings', { method: 'PUT', body: JSON.stringify({ key: 'fee_bps', value: feeBps }) });
      setFeeMsg('Platform fee updated ✓');
      setTimeout(() => setFeeMsg(''), 2000);
    } catch { setFeeMsg('Failed to save'); }
    finally { setFeeSaving(false); }
  }

  async function handleBoostSave() {
    setBoostSaving(true); setBoostMsg('');
    try {
      const boostBps = Math.round(boostPct * 100);
      await request('/admin/settings', { method: 'PUT', body: JSON.stringify({ key: 'boost_bps', value: boostBps }) });
      setBoostMsg('Boost rate updated ✓');
      setTimeout(() => setBoostMsg(''), 2000);
    } catch { setBoostMsg('Failed to save'); }
    finally { setBoostSaving(false); }
  }

  async function handleRunBoost() {
    if (!window.confirm('Run monthly boost batch now? This will award BTC to all users based on their fees paid this month.')) return;
    try {
      const res = await request('/admin/boost', { method: 'POST' });
      setBoostMsg(res.skipped ? 'Already ran this period' : `Boost complete — ${res.usersRewarded} users rewarded $${res.totalBoostUsd}`);
    } catch { setBoostMsg('Boost failed'); }
  }

  function formatValue(value, type) {
    if (type === 'cents') return (parseInt(value||0)/100).toFixed(2);
    return value;
  }

  function parseValue(input, type) {
    if (type === 'cents') return String(Math.round(parseFloat(input||0)*100));
    return input;
  }

  if (loading) return <div style={s.loading}>Loading...</div>;

  // Split configs into sections
  const limitConfigs = SETTINGS_CONFIG.filter(c => c.key.includes('limit') || c.key.includes('send_weekly'));
  const otherConfigs = SETTINGS_CONFIG.filter(c => !c.key.includes('limit') && !c.key.includes('send_weekly') && c.key !== 'maintenance_mode');
  const maintenanceConfig = SETTINGS_CONFIG.find(c => c.key === 'maintenance_mode');

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Platform Settings</h1>
          <p style={s.subtitle}>Control all platform variables from here</p>
        </div>
      </div>

      {/* Platform Fee */}
      <div style={s.feeCard}>
        <div style={s.feeHeader}>
          <div>
            <div style={s.feeTitle}>Platform Fee</div>
            <div style={s.feeDesc}>Applied to all transactions unless user has a custom rate</div>
          </div>
          <div style={s.feeBig}>{(isNaN(feePct) ? 0.50 : feePct).toFixed(2)}%</div>
        </div>
        <input type="range" min="0" max="2" step="0.05" value={isNaN(feePct) ? 0.50 : feePct} style={s.slider} onChange={e => setFeePct(parseFloat(e.target.value))}/>
        <div style={s.sliderTicks}><span>0%</span><span>0.5%</span><span>1%</span><span>1.5%</span><span>2%</span></div>
        <div style={s.presets}>
          {[0, 0.25, 0.50, 0.75, 1.00].map(pct => (
            <button key={pct} style={{...s.presetBtn, ...(Math.abs(feePct - pct) < 0.01 ? s.presetActive : {})}} onClick={() => setFeePct(pct)}>
              {pct === 0 ? 'Free' : `${pct.toFixed(2)}%`}
            </button>
          ))}
        </div>
        <div style={s.feePreview}>
          <div style={s.previewTitle}>Fee preview</div>
          <div style={s.previewGrid}>
            {[10, 50, 100, 500, 1000].map(amt => (
              <div key={amt} style={s.previewRow}>
                <span style={s.previewAmt}>${amt}</span>
                <span style={s.previewArrow}>→</span>
                <span style={s.previewFee}>${(amt * (isNaN(feePct) ? 0.50 : feePct) / 100).toFixed(2)} fee</span>
                <span style={s.previewNet}>${(amt - amt * (isNaN(feePct) ? 0.50 : feePct) / 100).toFixed(2)} net</span>
              </div>
            ))}
          </div>
        </div>
        <div style={s.feeFooter}>
          {feeMsg && <span style={{fontSize:13, color: feeMsg.includes('Failed') ? '#FF3B30' : '#00C48C', fontWeight:500}}>{feeMsg}</span>}
          <button style={s.saveBtn} onClick={handleFeeSave} disabled={feeSaving}>
            {feeSaving ? 'Saving...' : `Save ${(isNaN(feePct) ? 0.50 : feePct).toFixed(2)}% fee`}
          </button>
        </div>
      </div>

      {/* Boost Rate */}
      <div style={s.feeCard}>
        <div style={s.feeHeader}>
          <div>
            <div style={s.feeTitle}>⚡ Boost Rate</div>
            <div style={s.feeDesc}>% of fees returned to users as BTC each month</div>
          </div>
          <div style={{...s.feeBig, color:'#FF9500'}}>{(isNaN(boostPct) ? 20 : boostPct).toFixed(2)}%</div>
        </div>
        <input type="range" min="0" max="100" step="1" value={isNaN(boostPct) ? 20 : boostPct} style={{...s.slider, accentColor:'#FF9500'}} onChange={e => setBoostPct(parseFloat(e.target.value))}/>
        <div style={s.sliderTicks}><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div>
        <div style={s.presets}>
          {[0, 10, 20, 30, 50].map(pct => (
            <button key={pct} style={{...s.presetBtn, ...(Math.abs(boostPct - pct) < 0.5 ? {...s.presetActive, background:'#FFF5E6', borderColor:'#FF9500', color:'#FF9500'} : {})}} onClick={() => setBoostPct(pct)}>
              {pct === 0 ? 'Off' : `${pct}%`}
            </button>
          ))}
        </div>
        <div style={s.feeFooter}>
          {boostMsg && <span style={{fontSize:13, color: boostMsg.includes('Failed') ? '#FF3B30' : '#00C48C', fontWeight:500}}>{boostMsg}</span>}
          <div style={{display:'flex', gap:8}}>
            <button style={{...s.saveBtn, background:'#FF9500'}} onClick={handleBoostSave} disabled={boostSaving}>
              {boostSaving ? 'Saving...' : `Save ${boostPct.toFixed(0)}% boost`}
            </button>
            <button style={{...s.saveBtn, background:'#1D1D1F'}} onClick={handleRunBoost}>Run boost now</button>
          </div>
        </div>
      </div>

      {/* Transaction Limits */}
      <div style={s.sectionHeader}>
        <div style={s.sectionTitle}>Transaction Limits</div>
        <div style={s.sectionDesc}>Velocity and size limits — values are in USD</div>
      </div>
      <div style={s.grid}>
        {limitConfigs.map(config => {
          const rawValue = settings[config.key] || '0';
          return (
            <div key={config.key} style={s.card}>
              <div style={s.cardTitle}>{config.label}</div>
              <div style={s.cardDesc}>{config.description}</div>
              <div style={s.inputRow}>
                <div style={s.inputWrap}>
                  <span style={s.prefix}>$</span>
                  <input
                    style={s.input}
                    type="number"
                    min="0"
                    step="1"
                    defaultValue={formatValue(rawValue, config.type)}
                    onBlur={e => {
                      const parsed = parseValue(e.target.value, config.type);
                      setSettings(prev => ({...prev, [config.key]: parsed}));
                    }}
                  />
                </div>
                <button style={s.saveBtn} disabled={saving[config.key]} onClick={() => handleSave(config.key, settings[config.key])}>
                  {saving[config.key] ? 'Saving...' : 'Save'}
                </button>
              </div>
              {msgs[config.key] && (
                <div style={{fontSize:12, fontWeight:500, marginTop:8, color: msgs[config.key]==='Saved ✓' ? '#00C48C' : '#FF3B30'}}>
                  {msgs[config.key]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Minimums */}
      <div style={s.sectionHeader}>
        <div style={s.sectionTitle}>Minimums</div>
        <div style={s.sectionDesc}>Minimum transaction amounts — values are in USD</div>
      </div>
      <div style={s.grid}>
        {otherConfigs.map(config => {
          const rawValue = settings[config.key] || '0';
          return (
            <div key={config.key} style={s.card}>
              <div style={s.cardTitle}>{config.label}</div>
              <div style={s.cardDesc}>{config.description}</div>
              <div style={s.inputRow}>
                <div style={s.inputWrap}>
                  <span style={s.prefix}>$</span>
                  <input
                    style={s.input}
                    type="number"
                    min="0"
                    step="1"
                    defaultValue={formatValue(rawValue, config.type)}
                    onBlur={e => {
                      const parsed = parseValue(e.target.value, config.type);
                      setSettings(prev => ({...prev, [config.key]: parsed}));
                    }}
                  />
                </div>
                <button style={s.saveBtn} disabled={saving[config.key]} onClick={() => handleSave(config.key, settings[config.key])}>
                  {saving[config.key] ? 'Saving...' : 'Save'}
                </button>
              </div>
              {msgs[config.key] && (
                <div style={{fontSize:12, fontWeight:500, marginTop:8, color: msgs[config.key]==='Saved ✓' ? '#00C48C' : '#FF3B30'}}>
                  {msgs[config.key]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Maintenance Mode */}
      {maintenanceConfig && (
        <div style={s.dangerZone}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div>
              <div style={s.dangerTitle}>⚠️ Maintenance Mode</div>
              <div style={s.dangerDesc}>When enabled, ALL transactions are blocked platform-wide. Users will see a maintenance message.</div>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:12}}>
              <span style={{fontSize:14, fontWeight:500, color: settings[maintenanceConfig.key]==='true' ? '#FF3B30' : '#00C48C'}}>
                {settings[maintenanceConfig.key] === 'true' ? '🔴 Enabled' : '🟢 Disabled'}
              </span>
              <button
                style={{...s.saveBtn, background: settings[maintenanceConfig.key]==='true' ? '#FF3B30' : '#00C48C'}}
                onClick={() => {
                  const newVal = settings[maintenanceConfig.key] === 'true' ? 'false' : 'true';
                  setSettings(prev => ({...prev, [maintenanceConfig.key]: newVal}));
                  handleSave(maintenanceConfig.key, newVal);
                }}>
                {settings[maintenanceConfig.key] === 'true' ? 'Turn Off' : 'Turn On'}
              </button>
            </div>
          </div>
          {msgs[maintenanceConfig.key] && (
            <div style={{fontSize:12, fontWeight:500, marginTop:8, color: msgs[maintenanceConfig.key]==='Saved ✓' ? '#00C48C' : '#FF3B30'}}>
              {msgs[maintenanceConfig.key]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  loading:       { padding:40, color:'#6E6E73' },
  header:        { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title:         { fontSize:28, fontWeight:700 },
  subtitle:      { fontSize:14, color:'#6E6E73', marginTop:4 },
  sectionHeader: { marginBottom:16, marginTop:8 },
  sectionTitle:  { fontSize:18, fontWeight:600, color:'#1D1D1F' },
  sectionDesc:   { fontSize:13, color:'#6E6E73', marginTop:2 },
  feeCard:       { background:'#fff', borderRadius:20, padding:24, marginBottom:24, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  feeHeader:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 },
  feeTitle:      { fontSize:16, fontWeight:600, color:'#1D1D1F' },
  feeDesc:       { fontSize:13, color:'#6E6E73', marginTop:4 },
  feeBig:        { fontSize:40, fontWeight:700, color:'#007AFF' },
  slider:        { width:'100%', accentColor:'#007AFF', height:4, marginBottom:6 },
  sliderTicks:   { display:'flex', justifyContent:'space-between', fontSize:11, color:'#AEAEB2', marginBottom:16 },
  presets:       { display:'flex', gap:8, marginBottom:20 },
  presetBtn:     { padding:'7px 14px', background:'#F5F5F7', border:'1.5px solid transparent', borderRadius:8, fontSize:13, fontWeight:500, color:'#6E6E73', cursor:'pointer' },
  presetActive:  { background:'#EBF4FF', borderColor:'#007AFF', color:'#007AFF' },
  feePreview:    { background:'#F5F5F7', borderRadius:12, padding:16, marginBottom:16 },
  previewTitle:  { fontSize:12, fontWeight:600, color:'#6E6E73', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 },
  previewGrid:   { display:'flex', flexDirection:'column', gap:6 },
  previewRow:    { display:'flex', alignItems:'center', gap:12, fontSize:13 },
  previewAmt:    { fontWeight:600, width:48, color:'#1D1D1F' },
  previewArrow:  { color:'#AEAEB2' },
  previewFee:    { color:'#FF3B30', fontWeight:500, width:80 },
  previewNet:    { color:'#00C48C', fontWeight:500 },
  feeFooter:     { display:'flex', justifyContent:'space-between', alignItems:'center' },
  grid:          { display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:16, marginBottom:24 },
  card:          { background:'#fff', borderRadius:16, padding:20, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  cardTitle:     { fontSize:15, fontWeight:600, marginBottom:4 },
  cardDesc:      { fontSize:12, color:'#6E6E73', marginBottom:16 },
  inputRow:      { display:'flex', gap:8, alignItems:'center' },
  inputWrap:     { display:'flex', alignItems:'center', gap:4, background:'#F5F5F7', borderRadius:10, padding:'8px 12px', flex:1 },
  prefix:        { fontSize:14, fontWeight:600, color:'#6E6E73' },
  input:         { background:'transparent', border:'none', fontSize:16, fontWeight:600, outline:'none', width:'100%' },
  saveBtn:       { padding:'10px 16px', background:'#007AFF', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer' },
  dangerZone:    { background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:16, padding:20, marginBottom:24 },
  dangerTitle:   { fontSize:15, fontWeight:600, color:'#92400E', marginBottom:8 },
  dangerDesc:    { fontSize:13, color:'#92400E', lineHeight:1.6 },
};
